import React, { useEffect, useState, useCallback } from 'react';
import { GoogleMap, LoadScript, MarkerF } from '@react-google-maps/api';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { setorderAddress } from "../../slices/orderslice";

const libraries = ['places'];

const AddressSearchMap = ({ error }) => {

  const dispatch = useDispatch();
  const { slug } = useParams();
  const orderAddress = useSelector((state) => state.orderslice.orderAddress);
  const defaultLocation = { lat: 25.276987, lng: 55.296249 }; // Dubai
const [location, setLocation] = useState(defaultLocation);

  const [address, setAddress] = useState(orderAddress?.address || '');

  useEffect(() => {
    if (orderAddress?.address) {
      setAddress(orderAddress.address);
    } else {
      dispatch(setorderAddress({
        address: "",
        latitude: "",
        longitude: "",
      }));
    }
  }, [orderAddress?.address, dispatch]);
  //  Fetch address using Geocoder, returns formatted address
  const fetchAddress = useCallback((lat, lng) => {
    return new Promise((resolve) => {
      if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
        resolve('');
        return;
      }
      if (!lat || !lng) {
        resolve('Invalid coordinates');
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          const formattedAddress = results[0].formatted_address;
          resolve(formattedAddress);
        } else {
          resolve('No address found');
        }
      });
    });
  }, []);

  //  Update local state if orderAddress changes (slug present)
  useEffect(() => {
    if (orderAddress?.latitude && orderAddress?.longitude) {
      setLocation({
        lat: orderAddress.latitude,
        lng: orderAddress.longitude,
      });
      setAddress(orderAddress.address || '');
    }
  }, [orderAddress,]);

  //  On mount: if slug is not present, use current geolocation
  useEffect(() => {
    const getCurrentLocation = async () => {
      if (!slug) {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (pos) => {
              const { latitude, longitude } = pos.coords;
              const newPos = { lat: latitude, lng: longitude };
              setLocation(newPos);
              const fetched = await fetchAddress(latitude, longitude);
              setAddress(fetched);
              dispatch(setorderAddress({
                address: fetched,
                latitude: latitude,
                longitude: longitude,
              }));
            },
            async (err) => {
              setLocation(defaultLocation);
              const fetched = await fetchAddress(defaultLocation.lat, defaultLocation.lng);
              setAddress(fetched);
              dispatch(setorderAddress({
                address: fetched,
                latitude: defaultLocation.lat,
                longitude: defaultLocation.lng,
              }));
            }
          );
        } else {
          setLocation(defaultLocation);
          const fetched = await fetchAddress(defaultLocation.lat, defaultLocation.lng);
          setAddress(fetched);
          dispatch(setorderAddress({
            address: fetched,
            latitude: defaultLocation.lat,
            longitude: defaultLocation.lng,
          }));
        }
      }
    };

    getCurrentLocation();
  }, [slug, fetchAddress, dispatch]);

  //  Update Redux whenever address or location changes
  useEffect(() => {
    if (
      address !== orderAddress?.address ||
      location.lat !== orderAddress?.latitude ||
      location.lng !== orderAddress?.longitude
    ) {
      dispatch(setorderAddress({
        address: address,
        latitude: location.lat,
        longitude: location.lng,
      }));
    }
  }, [address, location,]);

  //  When selecting address from autocomplete
  const handleSelect = async (selected) => {
    try {
      const results = await geocodeByAddress(selected);
      const latLng = await getLatLng(results[0]);
      setLocation(latLng);
      setAddress(selected);
    } catch (error) {
    }
  };

  //  When dragging marker to new location
  const handleDragEnd = async (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    const newPos = { lat, lng };
    setLocation(newPos);
    const fetched = await fetchAddress(lat, lng);
    setAddress(fetched);
    dispatch(setorderAddress({
      address: fetched,
      latitude: lat,
      longitude: lng,
    }));
  };

  return (
    <div className="">
      <LoadScript
        googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        libraries={libraries}
      >
        <label className="form-label">Address</label>
        <PlacesAutocomplete value={address} onChange={setAddress} onSelect={handleSelect}>
          {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
            <div className="mb-3 position-relative">
              <input
                {...getInputProps({
                  placeholder: 'Search Address...',
                  className: 'form-control',
                  name: 'address',
                  id: "address"
                })}
              />
              {error && (

                <div className="custom-invalid-error" style={{ fontSize: "12px" }}>
                  {error}
                </div>
              )}
              <div
                className="autocomplete-dropdown-container position-absolute"
                style={{ zIndex: 1000, width: '100%' }}
              >
                {loading && <div>Loading...</div>}
                {suggestions.map(suggestion => (
                  <div
                    key={suggestion.placeId}
                    {...getSuggestionItemProps(suggestion)}
                    className="suggestion-item"
                    style={{ padding: '5px', cursor: 'pointer' }}
                  >
                    {suggestion.description}
                  </div>
                ))}
              </div>
            </div>
          )}
        </PlacesAutocomplete>

        <div style={{ height: '455px' }}>
          <GoogleMap
            center={location}
            zoom={19}
            mapContainerStyle={{ width: '100%', height: '300px' }}
          >
            <MarkerF
              position={location}
              draggable
              onDragEnd={handleDragEnd}
            />
          </GoogleMap>

          <div className="mt-2">
            <p><strong>Latitude:</strong> {location.lat}</p>
            <p><strong>Longitude:</strong> {location.lng}</p>
            <p><strong>Address:</strong> {address}</p>
          </div>
        </div>
      </LoadScript>
    </div>
  );
};
export default React.memo(AddressSearchMap);
